import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaTimes, FaStar, FaMapMarkerAlt, FaBolt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import { formatPrice } from "../../services/ticketingService";
import BlockClubLogo from "../../media/BC.png";
import "./SearchDropdown.css";

/**
 * SearchDropdown Component
 * Google-style search dropdown with Groupon-style result cards
 * Shows venues and events with pictures, details, and ratings
 */

// Helper function to extract percent from title (e.g., "20% off" -> 20)
function extractPercent(title) {
  if (!title) return null;
  const match = title.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : null;
}

// Helper function to parse price_text and return range object
function parsePriceLevel(priceText) {
  if (!priceText) return { low: 20, high: 30 };

  const rangeMatch = priceText.match(/\$(\d+)[–-]\$?(\d+)/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1], 10);
    const high = parseInt(rangeMatch[2], 10);
    return { low, high };
  }

  const singlePriceMatch = priceText.match(/\$(\d+)/);
  if (singlePriceMatch) {
    const price = parseInt(singlePriceMatch[1], 10);
    return { low: price, high: price };
  }

  const count = (priceText.match(/\$/g) || []).length;
  if (count > 0) {
    const priceMap = { 1: 15, 2: 25, 3: 40, 4: 60 };
    const price = priceMap[count] || 25;
    return { low: price, high: price };
  }

  return { low: 20, high: 30 };
}

// Calculate estimated dollar savings range
function calculateDollarSavingsRange(percent, priceRange) {
  if (!percent || !priceRange) return null;
  const lowSavings = Math.round((priceRange.low * percent) / 100);
  const highSavings = Math.round((priceRange.high * percent) / 100);
  return { low: lowSavings, high: highSavings };
}

function SearchDropdown({ userLocation, viewMode, email, customerToken, onResultClick }) {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const searchDebounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);

    searchDebounceRef.current = setTimeout(async () => {
      try {
        let url = `${apiUrl}/loyalty/search?query=${encodeURIComponent(searchQuery)}&limit=8`;

        // Add user location if available
        if (userLocation) {
          url += `&user_lat=${userLocation.lat}&user_lng=${userLocation.lng}`;
        }

        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results || []);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, apiUrl, userLocation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleResultClick = (result) => {
    if (result.result_type === "venue") {
      // Navigate to venue deals page
      if (viewMode === "public" || !email) {
        navigate(appendCompanyCode(`/coupons/public/venue/${result.venue_id}`));
      } else if (customerToken) {
        navigate(appendCompanyCode(`/coupons/${customerToken}/venue/${result.venue_id}`));
      }
    } else if (result.result_type === "event") {
      // Navigate to event detail page
      navigate(appendCompanyCode(`/events/${result.slug}`));
    }

    // Clear search and close dropdown
    setSearchQuery("");
    setShowDropdown(false);
    setIsSearchFocused(false);

    // Notify parent if callback provided
    if (onResultClick) {
      onResultClick(result);
    }
  };

  const formatAddress = (result) => {
    if (result.result_type === "venue") {
      const parts = [];
      if (result.address_1) parts.push(result.address_1);
      if (result.city) parts.push(result.city);
      if (result.state) parts.push(result.state);
      return parts.join(", ") || "Address not available";
    } else if (result.result_type === "event" && result.primary_venue) {
      const parts = [];
      if (result.primary_venue.address_1) parts.push(result.primary_venue.address_1);
      if (result.primary_venue.city) parts.push(result.primary_venue.city);
      return parts.join(", ") || "Address not available";
    }
    return "Address not available";
  };

  const getImageUrl = (result) => {
    if (result.result_type === "venue") {
      return result.poster_url || result.logo_url || BlockClubLogo;
    } else if (result.result_type === "event") {
      return result.hero_image_url || BlockClubLogo;
    }
    return BlockClubLogo;
  };

  const getTitle = (result) => {
    if (result.result_type === "venue") {
      return result.venue_name;
    } else if (result.result_type === "event") {
      return result.title;
    }
    return "";
  };

  const getSubtitle = (result) => {
    if (result.result_type === "venue") {
      return result.category || "Restaurant";
    } else if (result.result_type === "event") {
      return result.event_type ? result.event_type.replace(/_/g, " ") : "Event";
    }
    return "";
  };

  const getBestOffer = (result) => {
    if (result.result_type !== "venue" || !result.offers || result.offers.length === 0) {
      return null;
    }

    const offers = result.offers;
    const priceRange = parsePriceLevel(result.price_text);

    // Check for free offers
    const hasFreeEntry = offers.some(offer => {
      if (!offer.title) return false;
      const titleLower = offer.title.toLowerCase().trim();
      return titleLower === 'free' || titleLower.startsWith('free,') || titleLower.startsWith('free ');
    });

    if (hasFreeEntry) {
      return { type: 'free', display: 'FREE ENTRY' };
    }

    // Find best percentage offer
    let bestOffer = null;
    let bestSavings = 0;

    offers.forEach(offer => {
      const percent = extractPercent(offer.title);
      if (percent) {
        const savingsRange = calculateDollarSavingsRange(percent, priceRange);
        if (savingsRange && savingsRange.high > bestSavings) {
          bestSavings = savingsRange.high;
          bestOffer = { ...offer, percent, savingsRange };
        }
      }
    });

    if (bestOffer) {
      if (bestOffer.savingsRange.high >= 4) {
        const savingsText = bestOffer.savingsRange.low === bestOffer.savingsRange.high
          ? `~$${bestOffer.savingsRange.low}`
          : `~$${bestOffer.savingsRange.low}-${bestOffer.savingsRange.high}`;
        return {
          type: 'savings',
          display: `Save ${savingsText} (${bestOffer.percent}% off)`,
          percent: bestOffer.percent
        };
      } else {
        return {
          type: 'savings',
          display: `${bestOffer.percent}% off`,
          percent: bestOffer.percent
        };
      }
    }

    // If no percentage offer, show first offer title
    if (offers.length > 0 && offers[0].title) {
      return {
        type: 'title',
        display: offers[0].title
      };
    }

    return null;
  };

  const getPriceInfo = (result) => {
    if (result.result_type === "event") {
      const cents = result.min_tier_price_cents;
      if (cents !== null && cents !== undefined) {
        return cents === 0 ? "Free Entry" : `From ${formatPrice(cents)}`;
      }
      return "Free Event";
    }
    return "";
  };

  return (
    <div className="search-dropdown-container" ref={dropdownRef}>
      <div className={`search-dropdown-input-wrapper ${isSearchFocused ? "focused" : ""}`}>
        <FaSearch className="search-dropdown-icon" />
        <input
          type="text"
          placeholder="Search venues, events, categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            setIsSearchFocused(true);
            if (searchResults.length > 0) {
              setShowDropdown(true);
            }
          }}
          className="search-dropdown-input"
        />
        {searchQuery && (
          <button
            className="search-dropdown-clear-btn"
            onClick={handleClearSearch}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown-results">
          {isSearching ? (
            <div className="search-dropdown-loading">
              <div className="search-dropdown-spinner"></div>
              <span>Searching...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="search-dropdown-results-list">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.result_type}-${result.venue_id || result.event_id}-${index}`}
                  className="search-dropdown-result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="search-result-image-wrapper">
                    <img
                      src={getImageUrl(result)}
                      alt={getTitle(result)}
                      className="search-result-image"
                      onError={(e) => {
                        e.target.src = BlockClubLogo;
                      }}
                    />
                    {result.rating && result.rating > 0 && (
                      <div className="search-result-rating">
                        <FaStar className="rating-star" />
                        <span>{result.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>

                  <div className="search-result-content">
                    <div className="search-result-header">
                      <h4 className="search-result-title">{getTitle(result)}</h4>
                      <span className="search-result-type-badge">
                        {result.result_type === "event" ? (
                          <>Event</>
                        ) : (
                          <>{getSubtitle(result)}</>
                        )}
                      </span>
                    </div>

                    <div className="search-result-address">
                      <FaMapMarkerAlt />
                      <span>{formatAddress(result)}</span>
                      {result.distance && (
                        <span className="search-result-distance">
                          {result.distance} mi
                        </span>
                      )}
                    </div>

                    {result.result_type === "venue" ? (
                      <>
                        {(() => {
                          const bestOffer = getBestOffer(result);
                          return bestOffer ? (
                            <div className="search-result-deal">
                              <FaBolt className="deal-icon" />
                              <span className={`deal-text ${bestOffer.type === 'free' ? 'free-entry' : ''}`}>
                                {bestOffer.display}
                              </span>
                            </div>
                          ) : (
                            result.rating_count > 0 && (
                              <div className="search-result-footer">
                                <span className="search-result-reviews">
                                  {result.rating_count} {result.rating_count === 1 ? "review" : "reviews"}
                                </span>
                              </div>
                            )
                          );
                        })()}
                      </>
                    ) : (
                      <div className="search-result-footer">
                        <span className="search-result-price">{getPriceInfo(result)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="search-dropdown-no-results">
              <FaSearch />
              <p>No results found for "{searchQuery}"</p>
              <span>Try adjusting your search terms</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchDropdown;
